import type { Variants, Transition } from 'framer-motion';

import { transitionExit, transitionEnter } from './transition';

import type { VariantsTransitionOptions } from './transition';

// ----------------------------------------------------------------------

export type FadeDirection =
  | 'in'
  | 'inUp'
  | 'inDown'
  | 'inLeft'
  | 'inRight'
  | 'out'
  | 'outUp'
  | 'outDown'
  | 'outLeft'
  | 'outRight';

export type FadeOptions = VariantsTransitionOptions & { distance?: number };

export const varFade = (direction: FadeDirection, options?: FadeOptions): Variants => {
  const distance = options?.distance || 120;
  const transitionIn = options?.transitionIn;
  const transitionOut = options?.transitionOut;

  const variants: Record<FadeDirection, Variants> = {
    /**** In ****/
    in: {
      initial: { opacity: 0 },
      // 🚨 선존: 여기만 `transitionEnter`·`transitionExit` 를 **부르지 않고** 함수 자체를 넘긴다
      //    (다른 갈래는 전부 `transitionEnter(transitionIn)`). 동작을 그대로 두려고 캐스트만
      //    했다 — 처분은 A630 후보.
      animate: { opacity: 1, transition: transitionEnter as unknown as Transition },
      exit: { opacity: 0, transition: transitionExit as unknown as Transition },
    },
    inUp: {
      initial: { y: distance, opacity: 0 },
      animate: { y: 0, opacity: 1, transition: transitionEnter(transitionIn) },
      exit: {
        y: distance,
        opacity: 0,
        transition: transitionExit(transitionOut),
      },
    },
    inDown: {
      initial: { y: -distance, opacity: 0 },
      animate: { y: 0, opacity: 1, transition: transitionEnter(transitionIn) },
      exit: {
        y: -distance,
        opacity: 0,
        transition: transitionExit(transitionOut),
      },
    },
    inLeft: {
      initial: { x: -distance, opacity: 0 },
      animate: { x: 0, opacity: 1, transition: transitionEnter(transitionIn) },
      exit: {
        x: -distance,
        opacity: 0,
        transition: transitionExit(transitionOut),
      },
    },
    inRight: {
      initial: { x: distance, opacity: 0 },
      animate: { x: 0, opacity: 1, transition: transitionEnter(transitionIn) },
      exit: {
        x: distance,
        opacity: 0,
        transition: transitionExit(transitionOut),
      },
    },
    /**** Out ****/
    out: {
      initial: { opacity: 1 },
      animate: { opacity: 0, transition: transitionEnter(transitionIn) },
      exit: { opacity: 1, transition: transitionExit(transitionOut) },
    },
    outUp: {
      initial: { y: 0, opacity: 1 },
      animate: {
        y: -distance,
        opacity: 0,
        transition: transitionEnter(transitionIn),
      },
      exit: { y: 0, opacity: 1, transition: transitionExit(transitionOut) },
    },
    outDown: {
      initial: { y: 0, opacity: 1 },
      animate: {
        y: distance,
        opacity: 0,
        transition: transitionEnter(transitionIn),
      },
      exit: { y: 0, opacity: 1, transition: transitionExit(transitionOut) },
    },
    outLeft: {
      initial: { x: 0, opacity: 1 },
      animate: {
        x: -distance,
        opacity: 0,
        transition: transitionEnter(transitionIn),
      },
      exit: { x: 0, opacity: 1, transition: transitionExit(transitionOut) },
    },
    outRight: {
      initial: { x: 0, opacity: 1 },
      animate: {
        x: distance,
        opacity: 0,
        transition: transitionEnter(transitionIn),
      },
      exit: { x: 0, opacity: 1, transition: transitionExit(transitionOut) },
    },
  };

  return variants[direction];
};
